import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import useMediaQuery from '@mui/material/useMediaQuery';
import {useTheme} from '@mui/material/styles';

import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';

import {searchTypeLabel} from '@embertracking/common/data/search/types.mjs';

import useSearchQuery from './useSearchQuery';
import useRecentSearches from './useRecentSearches';
import {DROPDOWN_GROUP_LIMIT, MIN_QUERY_LENGTH} from './constants';
import {SearchTypeIcon, Highlight, resultPath, resultDate, resultHref, isModifiedClick, useCanOpenResult} from './searchPresentation';

// The ⌘K field in the shared Header (#1715).
//
// The problem this exists to solve: the only "search" in the app is the MUI
// DataGrid quick filter, which can only ever see rows the grid already fetched
// and columns the grid is currently showing. Training Records fetches a DATE
// RANGE (this year by default), so a 2023 record cannot match no matter what it
// says — and the grid answers "no results" rather than "not in this view", which
// reads as "the record doesn't exist". This field asks the server instead.

const EXPANDED_WIDTH = 460;
const COLLAPSED_WIDTH = 42;
// Half the collapsed height, so the closed field is a true circle and any wider
// state is a pill. It is expressed as a real number rather than the usual
// `999px` shorthand because this value is ANIMATED: the browser clamps a radius
// to half the box's height, so 999px renders identically to 21px and a 999→10
// interpolation sits above that clamp for 95% of its duration, then does the
// entire visible morph in the last few frames. Measured on the 280ms desktop
// transition: still 26px at 259ms, 10px by 309ms — a snap at the very end
// rather than a curve. From 21px it eases the whole way.
const COLLAPSED_RADIUS = COLLAPSED_WIDTH / 2;

const DROPDOWN_WIDTH = 560;

// navigator.platform is deprecated but still the only synchronous signal that
// works everywhere; userAgentData.platform is preferred where it exists.
const isMac = () => {
    if (typeof navigator === 'undefined') return false;
    const platform = navigator.userAgentData?.platform || navigator.platform || navigator.userAgent || '';
    return /mac|iphone|ipad|ipod/i.test(platform);
};

// Mac renders one glyph (⌘K); Windows and Linux need the word, and "CtrlK" in a
// single cap reads as a typo — so those platforms get two caps, which is how
// every other Windows app draws a chord.
const shortcutKeys = () => (isMac() ? ['⌘K'] : ['Ctrl', 'K']);
const shortcutTitle = () => (isMac() ? '⌘K' : 'Ctrl+K');

// A shortcut must not fire while someone is typing their name into a form.
const isTypingTarget = target => {
    if (!target) return false;
    if (target.isContentEditable) return true;
    return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
};

const KeyCap = ({children, sx}) => (
    <Box
        component="kbd"
        sx={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 11,
            fontWeight: 600,
            lineHeight: 1.4,
            color: 'text.secondary',
            bgcolor: 'grey.50',
            border: 1,
            borderColor: 'divider',
            borderRadius: '5px',
            px: 0.75,
            py: '2px',
            whiteSpace: 'nowrap',
            ...sx
        }}
    >
        {children}
    </Box>
);

const SectionLabel = ({children, count}) => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, px: 1.25, pt: 1, pb: 0.5}}>
        <Typography
            variant="caption"
            sx={{fontSize: 11, fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: 'text.secondary'}}
        >
            {children}
        </Typography>
        {count != null && (
            <Typography variant="caption" sx={{fontSize: 11, fontWeight: 600, color: 'text.secondary'}}>
                {count}
            </Typography>
        )}
    </Box>
);

// Every selectable line in the dropdown — a recent search, a recently-viewed
// record, or a result — is one of these, so arrow-key focus, hover and the
// aria-activedescendant wiring are written once rather than three times.
//
// A row that GOES somewhere renders as a real <a href>. That is what
// makes ⌘/Ctrl-click, middle-click, Shift-click and "Open link in new tab" from
// the context menu work at all — a div with an onClick has no URL for the
// browser to open, so every one of those gestures either did nothing or routed
// in place. Rows that only fill the field in (a recent search) stay divs;
// there's no destination to hand the browser.
const Row = ({index, focused, enabled = true, href, onActivate, onOpenInNewTab, children}) => {
    const link = enabled && Boolean(href);
    return (
        <Box
            component={link ? 'a' : 'div'}
            href={link ? href : undefined}
            id={`global-search-option-${index}`}
            role="option"
            aria-selected={focused}
            aria-disabled={!enabled}
            onMouseDown={event => {
                // Let the browser own its own gestures — preventDefault here
                // would cancel the middle-click new tab before it starts.
                if (isModifiedClick(event)) return;
                // Otherwise keep focus in the input so the click doesn't close
                // the dropdown out from under itself. A link row defers the
                // activation to onClick, where the default is cancellable.
                event.preventDefault();
                if (enabled && !link) onActivate();
            }}
            onClick={link ? event => {
                // The modified click already opened a tab (or started a save);
                // leaving the default alone is the entire point. The row still
                // counts as opened, though — a result you sent to a new tab
                // belongs in Recently viewed as much as one you clicked into.
                if (isModifiedClick(event)) {
                    onOpenInNewTab?.();
                    return;
                }
                // A plain click routes client-side instead of reloading the SPA.
                event.preventDefault();
                onActivate();
            } : undefined}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 1.25,
                py: 1.125,
                borderRadius: 1,
                cursor: enabled ? 'pointer' : 'default',
                color: 'inherit',
                textDecoration: 'none',
                bgcolor: focused ? 'action.hover' : 'transparent',
                outline: focused ? '2px solid' : 'none',
                outlineColor: 'primary.main',
                outlineOffset: '-2px',
                '&:hover': {bgcolor: 'action.hover'}
            }}
        >
            {children}
        </Box>
    );
};

// The subtitle line is the record's path followed by the snippet, so a row
// answers both "what is this?" and "why did it match?" at once — the field label
// ("Narrative", "Competencies") is what makes the second half legible.
const ResultContent = ({result, enabled}) => (
    <>
        <SearchTypeIcon type={result.type} sx={{fontSize: 18, color: 'text.secondary', width: 22, flexShrink: 0}} />
        <Box sx={{flex: 1, minWidth: 0}}>
            <Typography variant="body2" sx={{fontWeight: 500}} noWrap>
                <Highlight parts={result.highlight} />
            </Typography>
            <Typography variant="caption" sx={{display: 'block', color: 'text.secondary'}} noWrap>
                {resultPath(result, searchTypeLabel(result.type))}
                {result.snippet?.hit && (
                    <>
                        {' — '}
                        <Highlight parts={result.snippet} />
                    </>
                )}
            </Typography>
        </Box>
        {result.snippet?.tab && (
            <Chip size="small" label={result.snippet.label} sx={{height: 20, fontSize: 11.5, fontWeight: 600, flexShrink: 0}} />
        )}
        {/* The date is what separates a department's third "Relay Pumping"
            session from its first — without it, recurring training reads as the
            same row repeated. */}
        {resultDate(result) && (
            <Typography variant="caption" sx={{color: 'text.secondary', whiteSpace: 'nowrap', flexShrink: 0}}>
                {resultDate(result)}
            </Typography>
        )}
        {enabled && <KeyboardReturnIcon sx={{fontSize: 16, color: 'text.disabled', flexShrink: 0}} />}
    </>
);

export default function GlobalSearch() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    // Collapsed to a single icon until asked for. The header's job is the
    // breadcrumb; search is something you reach for, and a permanently-open
    // 460px field spends that space on the majority of page loads where nobody
    // searches at all. Clicking the icon (or ⌘K from anywhere) animates it out.
    const [expanded, setExpanded] = useState(false);
    // Tracked separately from `open` so the border and ring can ease from the
    // resting field colour to the focus colour. `open` flips in the same tick as
    // `expanded`, which left nothing to transition between — the border went
    // straight from transparent to primary and never passed through `divider` at
    // all. Real focus arrives a beat later (openSearch focuses on a timeout),
    // which is the beat the transition needs. It is also just more correct: a
    // focus ring should track focus, not whether a dropdown is showing.
    const [focused, setFocused] = useState(false);
    const [focusIndex, setFocusIndex] = useState(-1);

    const inputRef = useRef(null);
    const anchorRef = useRef(null);

    const {searches, records, rememberSearch, rememberRecord} = useRecentSearches();
    const canOpen = useCanOpenResult();

    const {loading, groups, total, truncated} = useSearchQuery(query, {
        limit: DROPDOWN_GROUP_LIMIT,
        enabled: open
    });

    const hasQuery = query.trim().length > 0;

    const closeSearch = useCallback(() => {
        // Back to the icon. The query is deliberately KEPT: an accidental click
        // outside shouldn't throw away what someone just typed, and re-opening
        // restores it mid-thought.
        setOpen(false);
        setExpanded(false);
        setFocusIndex(-1);
    }, []);

    const openSearch = useCallback(() => {
        setExpanded(true);
        setOpen(true);
        // The field animates open; focusing before it has width parks the caret
        // off-screen on iOS.
        setTimeout(() => inputRef.current?.focus(), 60);
    }, []);

    const searchHref = text => `/search?q=${encodeURIComponent(text)}`;

    // The footer's own URL, so "View all N results" is a link like every other
    // row rather than a click handler pretending to be one.
    const viewAllHref = searchHref(query.trim());

    const runSearch = useCallback(text => {
        const trimmed = (text ?? query).trim();
        if (trimmed.length < MIN_QUERY_LENGTH) return;
        rememberSearch(trimmed);
        navigate(searchHref(trimmed));
        closeSearch();
    }, [query, navigate, rememberSearch, closeSearch]);

    // Opening a result is two things — remembering it, and going there. They're
    // separate because a ⌘-click does the first here and leaves the second to
    // the browser.
    const rememberResult = useCallback(result => {
        rememberSearch(query);
        rememberRecord(result);
    }, [query, rememberRecord, rememberSearch]);

    const openResult = useCallback(result => {
        // Findable but not openable (e.g. training.read without training.write):
        // the row still tells you the record exists and what it said, but the
        // detail route doesn't exist for this member — see useCanOpenResult.
        if (!canOpen(result)) return;
        rememberResult(result);
        navigate(resultHref(result));
        setQuery('');
        closeSearch();
    }, [navigate, rememberResult, closeSearch, canOpen]);

    // These four are exhaustive whenever the field is open, which is the point:
    // the panel's CONTENT changes as a query resolves, but the panel itself never
    // disappears mid-keystroke. It used to — a request in flight with no results
    // yet matched none of the states, so the dropdown blinked out between "no
    // matches yet" and the answer.
    const showRecents = open && !hasQuery;
    const showResults = open && hasQuery && groups.length > 0;
    const showSearching = open && hasQuery && loading && groups.length === 0;
    const showEmpty = open && hasQuery && !loading && groups.length === 0;

    // ONE flat list of everything the arrow keys can land on, in render order.
    // The idle state is navigable for the same reason the results are: the
    // recents list is the fastest path back to a search you just ran, and
    // reaching for the mouse to click it defeats the point of a ⌘K field.
    const rows = useMemo(() => {
        if (showRecents) {
            return [
                ...searches.map(text => ({
                    key: `search:${text}`,
                    enabled: true,
                    activate: () => {
                        setQuery(text);
                        inputRef.current?.focus();
                    }
                })),
                ...records.map(record => ({
                    key: `record:${record.url}`,
                    enabled: true,
                    activate: () => {
                        navigate(record.url);
                        closeSearch();
                    }
                }))
            ];
        }
        return groups.flatMap(group => group.items).map(result => ({
            key: `${result.type}:${result.id}`,
            enabled: canOpen(result),
            activate: () => openResult(result)
        }));
    }, [showRecents, searches, records, groups, navigate, closeSearch, canOpen, openResult]);

    // Index of the first row of each rendered section, so the JSX below can map
    // its own items onto the flat list the keyboard walks.
    const recentSearchOffset = 0;
    const recentRecordOffset = searches.length;

    // A query change re-ranks everything, so the old highlight would be pointing
    // at a row that no longer exists.
    useEffect(() => setFocusIndex(-1), [query]);

    // ⌘K / Ctrl-K from anywhere, and `/` when not already typing. Registered on
    // the document because the point is that it works without aiming at the field.
    useEffect(() => {
        const onKeyDown = event => {
            // `!altKey` is a Windows detail, not decoration: AltGr reports as
            // ctrlKey + altKey there, so on a German/Polish/Nordic layout
            // AltGr+K types a character AND would have opened search, eating the
            // keystroke. Mac never hits this because ⌘ is its own modifier.
            const shortcut = (event.metaKey || event.ctrlKey)
                && !event.altKey
                && event.key.toLowerCase() === 'k';
            const slash = event.key === '/' && !event.metaKey && !event.ctrlKey && !event.altKey && !isTypingTarget(event.target);
            if (!shortcut && !slash) return;
            event.preventDefault();
            openSearch();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [openSearch]);

    const onKeyDown = event => {
        if (event.key === 'Escape') {
            event.preventDefault();
            closeSearch();
            inputRef.current?.blur();
            return;
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setOpen(true);
            setFocusIndex(index => Math.min(rows.length - 1, index + 1));
            return;
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            setFocusIndex(index => Math.max(-1, index - 1));
            return;
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            const focused = rows[focusIndex];
            // Enter with nothing highlighted means "search everything", which is
            // what the results page is for — and the only way to reach a hit past
            // each group's cap.
            if (focused?.enabled) focused.activate();
            else if (hasQuery) runSearch();
        }
    };

    const truncatedTypes = Object.keys(truncated || {});

    return (
        <ClickAwayListener onClickAway={closeSearch}>
            <Box
                ref={anchorRef}
                data-testid="global-search"
                // Read by the shared Header, which hides the breadcrumb trail
                // below md while this is open so the field gets the full row.
                data-search-expanded={expanded ? 'true' : 'false'}
                sx={theme => ({
                    // md and up is deliberately untouched from the original: a
                    // block box that sizes to the 460px field inside it. Making
                    // this a flex container cost the field 113px of that width —
                    // as a flex container its intrinsic size resolves against the
                    // child's `maxWidth: 100%`, and the 460 silently stopped
                    // applying.
                    position: 'relative',
                    flexShrink: 0,
                    minWidth: COLLAPSED_WIDTH,
                    // Below md only: collapsed it is a 42px circle at the end of
                    // the header row; open it grows into everything the hidden
                    // trail gave back, rather than sitting at a fixed width with
                    // dead space beside it. The basis has to be 0 rather than
                    // `auto` — on `auto` the field's max-content width
                    // (placeholder, key caps and all) becomes its base size, and
                    // with nothing left to shrink it pushes itself past the
                    // header's right padding and widens the whole document.
                    [theme.breakpoints.down('md')]: {
                        display: 'flex',
                        flex: expanded ? '1 1 0' : '0 0 auto',
                        minWidth: expanded ? 0 : COLLAPSED_WIDTH
                    }
                })}
            >
                <Tooltip title={expanded ? '' : `Search (${shortcutTitle()})`}>
                    <Box
                        onClick={() => !expanded && openSearch()}
                        role={expanded ? undefined : 'button'}
                        tabIndex={expanded ? undefined : 0}
                        aria-label={expanded ? undefined : 'Search'}
                        onKeyDown={event => {
                            if (expanded) return;
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                openSearch();
                            }
                        }}
                        data-testid="global-search-toggle"
                        sx={theme => ({
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            height: 42,
                            // Width is declared here for below-md and again in
                            // the up('md') block, rather than as a `{xs, md}`
                            // object. It has to be: MUI expands `{md: …}` into a
                            // key spelled exactly `@media (min-width:900px)` —
                            // the same string `theme.breakpoints.up('md')`
                            // produces — and assigns raw media keys rather than
                            // deep-merging them. The up('md') block below is
                            // declared later, so it silently replaced the whole
                            // generated block and took the 460px width with it,
                            // leaving the desktop field at `100%` of a
                            // shrink-to-fit parent (347px).
                            width: expanded ? '100%' : COLLAPSED_WIDTH,
                            maxWidth: '100%',
                            px: expanded ? `${12 - (focused ? 1 : 0)}px` : '10px',
                            borderRadius: expanded ? 1.25 : `${COLLAPSED_RADIUS}px`,
                            // The theme gives every MuiInputBase an opaque white
                            // background (customizations/inputs.js). Tint the
                            // container and that white paints over the middle of
                            // the field, leaving the tint visible ONLY behind the
                            // search icon and the ⌘K cap — two grey blocks with a
                            // white gap between them. So the container matches the
                            // input's own colour, and the input is forced
                            // transparent below so the two can't drift apart again.
                            bgcolor: expanded ? 'background.paper' : 'transparent',
                            // Focused, this is meant to read as exactly the same
                            // control as any other text field on the page, so it
                            // wears what MuiOutlinedInput wears when focused —
                            // measured rather than guessed: 2px solid
                            // primary.main, no shadow, no outline.
                            //
                            // The extra pixel is taken out of the horizontal
                            // padding rather than added to the box, so the icon
                            // and the caret don't step sideways on focus. An
                            // outlined TextField dodges this by painting its
                            // border on a separate absolutely-positioned
                            // fieldset; this field has no such element, so it
                            // pays for the width itself. `padding` is already in
                            // the transition list, so the two move together.
                            borderStyle: 'solid',
                            borderWidth: expanded && focused ? 2 : 1,
                            borderColor: !expanded ? 'transparent' : (focused ? 'primary.main' : 'divider'),
                            boxShadow: 'none',
                            overflow: 'hidden',
                            cursor: expanded ? 'text' : 'pointer',
                            '&:hover': expanded ? undefined : {bgcolor: 'action.hover'},
                            '@keyframes etSearchFadeIn': {
                                from: {opacity: 0},
                                to: {opacity: 1}
                            },
                            // Desktop keeps the slide: the field grows in place
                            // from a 42px circle to 460px, a short move with the
                            // rest of the row untouched.
                            //
                            // On a phone that same animation was three things
                            // disagreeing at once — the field sliding the width of
                            // the header, its radius morphing pill→rect, and the
                            // breadcrumb hard-cutting out from under it, all while
                            // the dropdown sat at full width beside a half-grown
                            // field. Animating the geometry is what made each of
                            // those visible. So here it simply arrives at its
                            // final size and fades, which reads as one movement
                            // instead of four.
                            [theme.breakpoints.up('md')]: {
                                width: expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
                                transition: theme.transitions.create(
                                    ['width', 'border-radius', 'padding', 'background-color', 'border-color', 'border-width'],
                                    {duration: 280, easing: theme.transitions.easing.easeInOut}
                                )
                            },
                            [theme.breakpoints.down('md')]: {
                                // Colour only. The geometry deliberately doesn't
                                // move here (see the fade below), but the border
                                // still has somewhere to travel from and to.
                                transition: theme.transitions.create(
                                    ['border-color', 'border-width', 'padding'],
                                    {duration: 280, easing: theme.transitions.easing.easeInOut}
                                ),
                                // A transition can't fade this in: at the moment
                                // `expanded` flips, the element is already on
                                // screen at full opacity as the collapsed circle,
                                // so there is no from-value to animate. A keyframe
                                // run on the state change has one.
                                ...(expanded && {animation: 'etSearchFadeIn 160ms ease-out'})
                            }
                        })}
                    >
                        {/* The magnifier NEVER moves, resizes or swaps out — it
                            just breathes while a request is in flight. Earlier
                            attempts (a spinner in its place, then a progress bar
                            on the bottom edge) both drew the eye to a different
                            part of the field on every keystroke. Opacity costs no
                            layout and no repaint of anything around it. */}
                        <SearchIcon
                            sx={{
                                fontSize: 21,
                                color: 'text.secondary',
                                flexShrink: 0,
                                '@keyframes etSearchPulse': {
                                    '0%, 100%': {opacity: 1},
                                    '50%': {opacity: 0.35}
                                },
                                ...(loading && expanded && hasQuery && {
                                    animation: 'etSearchPulse 1.1s ease-in-out infinite'
                                })
                            }}
                        />

                        <Box
                            sx={theme => ({
                                flex: 1,
                                minWidth: 0,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                opacity: expanded ? 1 : 0,
                                transition: theme.transitions.create('opacity', {duration: 200}),
                                // Below md the field itself fades in as a whole,
                                // so fading its contents again on a slower curve
                                // just leaves the placeholder trailing behind the
                                // box it sits in.
                                [theme.breakpoints.down('md')]: {transition: 'none'}
                            })}
                        >
                            <InputBase
                                inputRef={inputRef}
                                value={query}
                                onChange={event => {
                                    setQuery(event.target.value);
                                    setOpen(true);
                                }}
                                onFocus={() => {
                                    setOpen(true);
                                    setFocused(true);
                                }}
                                onBlur={() => setFocused(false)}
                                onKeyDown={onKeyDown}
                                placeholder={isMobile ? 'Search…' : 'Search incidents, training, users, apparatus…'}
                                inputProps={{
                                    'aria-label': 'Search',
                                    'data-testid': 'global-search-input',
                                    role: 'combobox',
                                    'aria-expanded': open,
                                    'aria-controls': 'global-search-listbox',
                                    'aria-activedescendant': focusIndex >= 0 ? `global-search-option-${focusIndex}` : undefined
                                }}
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    fontSize: 14,
                                    // See the container's bgcolor note: the field
                                    // owns the background, the input never does.
                                    background: 'transparent',
                                    minHeight: 0
                                }}
                            />
                            {hasQuery ? (
                                <Tooltip title="Clear">
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            setQuery('');
                                            inputRef.current?.focus();
                                        }}
                                        sx={{flexShrink: 0, p: 0.25}}
                                    >
                                        <CloseIcon sx={{fontSize: 18}} />
                                    </IconButton>
                                </Tooltip>
                            ) : (
                                <Box sx={{display: {xs: 'none', md: 'flex'}, alignItems: 'center', gap: 0.375, flexShrink: 0}}>
                                    {shortcutKeys().map(key => <KeyCap key={key}>{key}</KeyCap>)}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Tooltip>

                <Popper
                    open={open}
                    anchorEl={anchorRef.current}
                    placement="bottom-end"
                    // The header sits above the page but below MUI's modals; the
                    // dropdown has to clear the header's own stacking context.
                    sx={{zIndex: theme => theme.zIndex.appBar + 2}}
                    modifiers={[{name: 'offset', options: {offset: [0, 8]}}]}
                >
                    <Paper
                        elevation={8}
                        id="global-search-listbox"
                        role="listbox"
                        data-testid="global-search-results"
                        sx={theme => ({
                            // Popper aligns the panel's right edge to the field's;
                            // below md the field runs the width of the header, so
                            // the panel matches it and lands on the same gutters.
                            width: {xs: 'calc(100vw - 24px)', md: DROPDOWN_WIDTH},
                            maxWidth: '100%',
                            borderRadius: 1.5,
                            border: 1,
                            borderColor: 'divider',
                            overflow: 'hidden',
                            '@keyframes etSearchPanelFadeIn': {
                                from: {opacity: 0},
                                to: {opacity: 1}
                            },
                            // Arrives with the field rather than ahead of it.
                            [theme.breakpoints.down('md')]: {
                                animation: 'etSearchPanelFadeIn 160ms ease-out'
                            }
                        })}
                    >
                        <Box sx={{maxHeight: 460, overflowY: 'auto', p: 1}}>
                            {showRecents && (
                                <>
                                    {searches.length > 0 && (
                                        <>
                                            <SectionLabel>Recent searches</SectionLabel>
                                            {searches.map((text, index) => {
                                                const at = recentSearchOffset + index;
                                                return (
                                                    <Row
                                                        key={text}
                                                        index={at}
                                                        focused={focusIndex === at}
                                                        onActivate={rows[at].activate}
                                                    >
                                                        <HistoryIcon sx={{fontSize: 18, color: 'text.secondary', width: 22}} />
                                                        <Typography variant="body2" sx={{flex: 1}} noWrap>{text}</Typography>
                                                    </Row>
                                                );
                                            })}
                                        </>
                                    )}

                                    {records.length > 0 && (
                                        <>
                                            {searches.length > 0 && <Divider sx={{my: 1, mx: 1.25}} />}
                                            <SectionLabel>Recently viewed</SectionLabel>
                                            {records.map((record, index) => {
                                                const at = recentRecordOffset + index;
                                                return (
                                                    <Row
                                                        key={record.url}
                                                        index={at}
                                                        focused={focusIndex === at}
                                                        href={record.url}
                                                        onActivate={rows[at].activate}
                                                    >
                                                        <SearchTypeIcon type={record.type} sx={{fontSize: 18, color: 'text.secondary', width: 22}} />
                                                        <Box sx={{flex: 1, minWidth: 0}}>
                                                            <Typography variant="body2" sx={{fontWeight: 500}} noWrap>{record.title}</Typography>
                                                            <Typography variant="caption" sx={{display: 'block', color: 'text.secondary'}} noWrap>
                                                                {resultPath(record, searchTypeLabel(record.type), {withType: true})}
                                                            </Typography>
                                                        </Box>
                                                    </Row>
                                                );
                                            })}
                                        </>
                                    )}

                                    {searches.length === 0 && records.length === 0 && (
                                        <Typography variant="body2" sx={{p: 3, textAlign: 'center', color: 'text.secondary'}}>
                                            Search incidents, training records, users, apparatus, reports and settings.
                                        </Typography>
                                    )}
                                </>
                            )}

                            {showResults && groups.map(group => {
                                const offset = rows.findIndex(row => row.key === `${group.items[0].type}:${group.items[0].id}`);
                                return (
                                    <Box key={group.type} sx={{pb: 0.5}}>
                                        <SectionLabel count={group.total > group.count ? `${group.count} of ${group.total}` : group.total}>
                                            {group.label}
                                        </SectionLabel>
                                        {group.items.map((result, index) => {
                                            const at = offset + index;
                                            return (
                                                <Row
                                                    key={`${result.type}-${result.id}`}
                                                    index={at}
                                                    focused={focusIndex === at}
                                                    enabled={canOpen(result)}
                                                    href={resultHref(result)}
                                                    onActivate={() => openResult(result)}
                                                    onOpenInNewTab={() => rememberResult(result)}
                                                >
                                                    <ResultContent result={result} enabled={canOpen(result)} />
                                                </Row>
                                            );
                                        })}
                                    </Box>
                                );
                            })}

                            {showSearching && (
                                <Typography variant="body2" sx={{p: 3, textAlign: 'center', color: 'text.secondary'}}>
                                    Searching…
                                </Typography>
                            )}

                            {showEmpty && (
                                <Typography variant="body2" sx={{p: 3, textAlign: 'center', color: 'text.secondary'}}>
                                    No matches. Press ↵ to search everything.
                                </Typography>
                            )}

                            {truncatedTypes.length > 0 && showResults && (
                                <Typography variant="caption" sx={{display: 'block', px: 1.25, py: 1, color: 'warning.dark'}}>
                                    This department has more records than search can hold, so some older{' '}
                                    {truncatedTypes.map(type => searchTypeLabel(type).toLowerCase()).join(' and ')} were not searched.
                                </Typography>
                            )}
                        </Box>

                        <Box
                            sx={{
                                borderTop: 1,
                                borderColor: 'divider',
                                bgcolor: 'action.hover',
                                px: 1.5,
                                py: 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.75
                            }}
                        >
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.75, flexWrap: 'wrap'}}>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                                    <KeyCap>↑↓</KeyCap>
                                    <Typography variant="caption" sx={{color: 'text.secondary'}}>Navigate</Typography>
                                </Box>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                                    <KeyCap>↵</KeyCap>
                                    <Typography variant="caption" sx={{color: 'text.secondary'}}>Open</Typography>
                                </Box>
                                <Box sx={{display: {xs: 'none', sm: 'flex'}, alignItems: 'center', gap: 0.75}}>
                                    <KeyCap>esc</KeyCap>
                                    <Typography variant="caption" sx={{color: 'text.secondary'}}>Dismiss</Typography>
                                </Box>
                            </Box>
                            <Box sx={{flex: 1}} />
                            {hasQuery && (
                                <Typography
                                    variant="caption"
                                    component="a"
                                    href={viewAllHref}
                                    onMouseDown={event => {
                                        // Same contract as a result Row: the
                                        // browser keeps its modified clicks, a
                                        // plain one stays inside the SPA.
                                        if (isModifiedClick(event)) return;
                                        event.preventDefault();
                                    }}
                                    onClick={event => {
                                        if (isModifiedClick(event)) return;
                                        event.preventDefault();
                                        runSearch();
                                    }}
                                    sx={{fontWeight: 600, color: 'info.main', cursor: 'pointer', whiteSpace: 'nowrap', textDecoration: 'none'}}
                                    data-testid="global-search-view-all"
                                >
                                    {total ? `View all ${total} results  ↵` : 'Search everything  ↵'}
                                </Typography>
                            )}
                        </Box>
                    </Paper>
                </Popper>
            </Box>
        </ClickAwayListener>
    );
}
